/**
 * Diff Viewer - Eliza Forge Design System
 *
 * GitHub-quality text diff with side-by-side/unified modes,
 * word-level highlighting, collapsible unchanged regions, and locked scroll.
 */

import * as React from "react"
import * as Diff from "diff"
import { cn } from "../../shared/lib/cn"
import { Skeleton } from "./skeleton"

/* ============================================
   TYPES
   ============================================ */

type DiffMode = "side-by-side" | "unified"

interface DiffViewerProps {
  oldText: string
  newText: string
  oldLabel?: string
  newLabel?: string
  mode?: DiffMode
  onModeChange?: (mode: DiffMode) => void
  contextLines?: number
  collapseThreshold?: number
  loading?: boolean
  emptyMessage?: string
  className?: string
}

interface DiffStatsProps {
  added: number
  removed: number
  unchanged: number
  className?: string
}

interface HunkData {
  type: "hunk"
  oldStart: number
  newStart: number
  lines: LineData[]
}

interface CollapsedData {
  type: "collapsed"
  count: number
  oldStart: number
  newStart: number
  lines: LineData[]
}

interface LineData {
  type: "added" | "removed" | "unchanged"
  content: string
  oldLineNo?: number
  newLineNo?: number
  wordDiff?: WordSpan[]
}

interface WordSpan {
  text: string
  type: "added" | "removed" | "unchanged"
}

type Section = HunkData | CollapsedData

/* ============================================
   DIFF COMPUTATION
   ============================================ */

function computeDiff(oldText: string, newText: string): LineData[] {
  const changes = Diff.diffLines(oldText, newText)
  const lines: LineData[] = []
  let oldLine = 1
  let newLine = 1

  for (const change of changes) {
    const value = change.value.replace(/\n$/, "")
    const sublines = value.split("\n")

    for (const text of sublines) {
      if (change.added) {
        lines.push({ type: "added", content: text, newLineNo: newLine++ })
      } else if (change.removed) {
        lines.push({ type: "removed", content: text, oldLineNo: oldLine++ })
      } else {
        lines.push({
          type: "unchanged",
          content: text,
          oldLineNo: oldLine++,
          newLineNo: newLine++,
        })
      }
    }
  }

  return lines
}

function computeWordDiff(removed: LineData[], added: LineData[]): void {
  const oldText = removed.map(l => l.content).join("\n")
  const newText = added.map(l => l.content).join("\n")
  const wordChanges = Diff.diffWordsWithSpace(oldText, newText)

  const oldSpans: WordSpan[] = []
  const newSpans: WordSpan[] = []

  for (const change of wordChanges) {
    if (change.added) {
      newSpans.push({ text: change.value, type: "added" })
    } else if (change.removed) {
      oldSpans.push({ text: change.value, type: "removed" })
    } else {
      oldSpans.push({ text: change.value, type: "unchanged" })
      newSpans.push({ text: change.value, type: "unchanged" })
    }
  }

  if (removed.length === 1) removed[0].wordDiff = oldSpans
  if (added.length === 1) added[0].wordDiff = newSpans
}

function groupIntoSections(
  lines: LineData[],
  contextLines: number,
  collapseThreshold: number
): Section[] {
  const changed = new Set<number>()
  lines.forEach((l, i) => {
    if (l.type !== "unchanged") changed.add(i)
  })

  const visible = new Set<number>()
  changed.forEach(i => {
    for (let j = Math.max(0, i - contextLines); j <= Math.min(lines.length - 1, i + contextLines); j++) {
      visible.add(j)
    }
  })

  // Attach word-level diffs to consecutive removed+added pairs
  let i = 0
  while (i < lines.length) {
    if (lines[i].type === "removed") {
      const removedBlock: LineData[] = []
      while (i < lines.length && lines[i].type === "removed") removedBlock.push(lines[i++])
      const addedBlock: LineData[] = []
      while (i < lines.length && lines[i].type === "added") addedBlock.push(lines[i++])
      if (removedBlock.length > 0 && addedBlock.length > 0) {
        computeWordDiff(removedBlock, addedBlock)
      }
    } else {
      i++
    }
  }

  if (changed.size === 0) {
    if (lines.length > collapseThreshold) {
      return [{
        type: "collapsed",
        count: lines.length,
        oldStart: lines[0]?.oldLineNo ?? 1,
        newStart: lines[0]?.newLineNo ?? 1,
        lines,
      }]
    }
    return [{
      type: "hunk",
      oldStart: lines[0]?.oldLineNo ?? 1,
      newStart: lines[0]?.newLineNo ?? 1,
      lines,
    }]
  }

  const sections: Section[] = []
  let currentHunk: LineData[] = []
  let collapsedLines: LineData[] = []

  for (let idx = 0; idx < lines.length; idx++) {
    if (visible.has(idx)) {
      if (collapsedLines.length > 0) {
        if (collapsedLines.length > collapseThreshold) {
          sections.push({
            type: "collapsed",
            count: collapsedLines.length,
            oldStart: collapsedLines[0]?.oldLineNo ?? 1,
            newStart: collapsedLines[0]?.newLineNo ?? 1,
            lines: collapsedLines,
          })
        } else {
          currentHunk.push(...collapsedLines)
        }
        collapsedLines = []
      }
      currentHunk.push(lines[idx])
    } else {
      if (currentHunk.length > 0) {
        sections.push({
          type: "hunk",
          oldStart: currentHunk[0]?.oldLineNo ?? currentHunk[0]?.newLineNo ?? 1,
          newStart: currentHunk[0]?.newLineNo ?? currentHunk[0]?.oldLineNo ?? 1,
          lines: currentHunk,
        })
        currentHunk = []
      }
      collapsedLines.push(lines[idx])
    }
  }

  if (currentHunk.length > 0) {
    sections.push({
      type: "hunk",
      oldStart: currentHunk[0]?.oldLineNo ?? 1,
      newStart: currentHunk[0]?.newLineNo ?? 1,
      lines: currentHunk,
    })
  }

  if (collapsedLines.length > 0) {
    if (collapsedLines.length > collapseThreshold) {
      sections.push({
        type: "collapsed",
        count: collapsedLines.length,
        oldStart: collapsedLines[0]?.oldLineNo ?? 1,
        newStart: collapsedLines[0]?.newLineNo ?? 1,
        lines: collapsedLines,
      })
    } else if (sections.length > 0 && sections[sections.length - 1].type === "hunk") {
      (sections[sections.length - 1] as HunkData).lines.push(...collapsedLines)
    }
  }

  return sections
}

/* ============================================
   WORD SPANS RENDERER
   ============================================ */

const WordSpans: React.FC<{ spans: WordSpan[]; lineType: "added" | "removed" }> = ({
  spans,
  lineType,
}) => (
  <>
    {spans.map((span, i) => {
      if (span.type === "unchanged") {
        return <span key={i}>{span.text}</span>
      }
      const highlight =
        span.type === "added"
          ? "bg-green-200 dark:bg-green-800 rounded-sm"
          : span.type === "removed"
            ? "bg-red-200 dark:bg-red-800 rounded-sm"
            : ""

      if (
        (lineType === "added" && span.type === "added") ||
        (lineType === "removed" && span.type === "removed")
      ) {
        return (
          <span key={i} className={highlight}>
            {span.text}
          </span>
        )
      }
      return <span key={i}>{span.text}</span>
    })}
  </>
)

/* ============================================
   DIFF LINE
   ============================================ */

const lineClasses = {
  added: "bg-green-50 dark:bg-green-950",
  removed: "bg-red-50 dark:bg-red-950",
  unchanged: "",
}

const DiffLine: React.FC<{
  line: LineData
  showOldLineNo?: boolean
  showNewLineNo?: boolean
  unified?: boolean
}> = ({ line, showOldLineNo = true, showNewLineNo = true, unified = false }) => {
  const marker = line.type === "added" ? "+" : line.type === "removed" ? "-" : " "

  return (
    <div className={cn("flex font-mono text-xs leading-6", lineClasses[line.type])}>
      {showOldLineNo && (
        <span className="w-12 shrink-0 select-none text-right pr-2 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-dark-surface-2">
          {line.oldLineNo ?? ""}
        </span>
      )}
      {showNewLineNo && (
        <span className="w-12 shrink-0 select-none text-right pr-2 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-dark-surface-2">
          {line.newLineNo ?? ""}
        </span>
      )}
      {unified && (
        <span className="w-5 shrink-0 select-none text-center text-gray-400 dark:text-gray-500">
          {marker}
        </span>
      )}
      <span className="flex-1 px-3 whitespace-pre-wrap break-all">
        {line.wordDiff ? (
          <WordSpans spans={line.wordDiff} lineType={line.type as "added" | "removed"} />
        ) : (
          line.content || "\u00A0"
        )}
      </span>
    </div>
  )
}

/* ============================================
   COLLAPSED REGION
   ============================================ */

const DiffCollapsedRegion: React.FC<{
  count: number
  onExpand: () => void
}> = ({ count, onExpand }) => (
  <button
    type="button"
    onClick={onExpand}
    className={cn(
      "w-full py-1.5 text-center text-small text-gray-500 dark:text-gray-400",
      "bg-gray-50 dark:bg-dark-surface border-y border-gray-200 dark:border-dark-border",
      "hover:bg-gray-100 dark:hover:bg-dark-surface-2 transition-colors cursor-pointer"
    )}
  >
    Show {count} unchanged line{count !== 1 ? "s" : ""}
  </button>
)

/* ============================================
   SIDE-BY-SIDE RENDERER
   ============================================ */

const SideBySideView: React.FC<{ lines: LineData[] }> = ({ lines }) => {
  const leftRef = React.useRef<HTMLDivElement>(null)
  const rightRef = React.useRef<HTMLDivElement>(null)
  const syncing = React.useRef(false)

  const syncScroll = React.useCallback(
    (source: HTMLDivElement | null, target: HTMLDivElement | null) => {
      if (!source || !target || syncing.current) return
      syncing.current = true
      target.scrollTop = source.scrollTop
      requestAnimationFrame(() => {
        syncing.current = false
      })
    },
    []
  )

  const leftLines = lines.filter(l => l.type !== "added")
  const rightLines = lines.filter(l => l.type !== "removed")

  return (
    <div className="flex divide-x divide-gray-200 dark:divide-dark-border">
      <div
        ref={leftRef}
        className="w-1/2 overflow-auto"
        onScroll={() => syncScroll(leftRef.current, rightRef.current)}
      >
        {leftLines.map((line, i) => (
          <DiffLine key={`l-${i}`} line={line} showOldLineNo showNewLineNo={false} />
        ))}
      </div>
      <div
        ref={rightRef}
        className="w-1/2 overflow-auto"
        onScroll={() => syncScroll(rightRef.current, leftRef.current)}
      >
        {rightLines.map((line, i) => (
          <DiffLine key={`r-${i}`} line={line} showOldLineNo={false} showNewLineNo />
        ))}
      </div>
    </div>
  )
}

/* ============================================
   UNIFIED RENDERER
   ============================================ */

const UnifiedView: React.FC<{ lines: LineData[] }> = ({ lines }) => (
  <div>
    {lines.map((line, i) => (
      <DiffLine key={i} line={line} showOldLineNo showNewLineNo unified />
    ))}
  </div>
)

/* ============================================
   DIFF STATS
   ============================================ */

const DiffStats: React.FC<DiffStatsProps> = ({ added, removed, unchanged, className }) => (
  <div className={cn("flex items-center gap-3 text-small font-mono", className)}>
    {added > 0 && <span className="text-green-600 dark:text-green-400">+{added}</span>}
    {removed > 0 && <span className="text-red-600 dark:text-red-400">-{removed}</span>}
    <span className="text-gray-500 dark:text-gray-400">{unchanged} unchanged</span>
  </div>
)

/* ============================================
   MODE PERSISTENCE
   ============================================ */

const STORAGE_KEY = "eliza-diff-mode"

function getPersistedMode(): DiffMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === "side-by-side" || stored === "unified") return stored
  } catch {}
  return "side-by-side"
}

function persistMode(mode: DiffMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {}
}

/* ============================================
   DIFF VIEWER — main component
   ============================================ */

const DiffViewer: React.FC<DiffViewerProps> = ({
  oldText,
  newText,
  oldLabel = "Original",
  newLabel = "Modified",
  mode: controlledMode,
  onModeChange,
  contextLines = 3,
  collapseThreshold = 5,
  loading,
  emptyMessage = "No changes",
  className,
}) => {
  const [internalMode, setInternalMode] = React.useState<DiffMode>(getPersistedMode)
  const mode = controlledMode ?? internalMode
  const setMode = React.useCallback(
    (m: DiffMode) => {
      setInternalMode(m)
      persistMode(m)
      onModeChange?.(m)
    },
    [onModeChange]
  )

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set())

  if (loading) {
    return <Skeleton variant="rounded" height={300} className={className} />
  }

  const lines = computeDiff(oldText, newText)
  const sections = groupIntoSections(lines, contextLines, collapseThreshold)

  const added = lines.filter(l => l.type === "added").length
  const removed = lines.filter(l => l.type === "removed").length
  const unchanged = lines.filter(l => l.type === "unchanged").length

  if (added === 0 && removed === 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-12 text-small text-gray-500 dark:text-gray-400", className)}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="mb-2 text-green-500">
          <path d="M5 10L9 14L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className={cn("rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-dark-surface-2 border-b border-gray-200 dark:border-dark-border">
        <div className="flex items-center gap-4 text-small text-gray-500 dark:text-gray-400">
          <span>{oldLabel}</span>
          <span>→</span>
          <span>{newLabel}</span>
        </div>
        <div className="flex items-center gap-3">
          <DiffStats added={added} removed={removed} unchanged={unchanged} />
          <div className="flex rounded-md border border-gray-200 dark:border-dark-border overflow-hidden">
            <button
              type="button"
              onClick={() => setMode("side-by-side")}
              className={cn(
                "px-3 py-1 text-xs font-medium transition-colors",
                mode === "side-by-side"
                  ? "bg-charcoal text-white dark:bg-gray-200 dark:text-charcoal"
                  : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-dark-surface dark:text-gray-400 dark:hover:bg-dark-surface-2"
              )}
            >
              Split
            </button>
            <button
              type="button"
              onClick={() => setMode("unified")}
              className={cn(
                "px-3 py-1 text-xs font-medium transition-colors",
                mode === "unified"
                  ? "bg-charcoal text-white dark:bg-gray-200 dark:text-charcoal"
                  : "bg-white text-gray-600 hover:bg-gray-50 dark:bg-dark-surface dark:text-gray-400 dark:hover:bg-dark-surface-2"
              )}
            >
              Unified
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-auto max-h-[600px]">
        {sections.map((section, idx) => {
          if (section.type === "collapsed" && !expanded.has(idx)) {
            return (
              <DiffCollapsedRegion
                key={`collapsed-${idx}`}
                count={section.count}
                onExpand={() => setExpanded(prev => new Set(prev).add(idx))}
              />
            )
          }

          const sectionLines =
            section.type === "collapsed" ? section.lines : section.lines

          return (
            <div key={`hunk-${idx}`}>
              {section.type === "hunk" && idx > 0 && (
                <div className="px-4 py-1 text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-dark-surface border-y border-gray-200 dark:border-dark-border">
                  @@ -{section.oldStart} +{section.newStart} @@
                </div>
              )}
              {mode === "side-by-side" ? (
                <SideBySideView lines={sectionLines} />
              ) : (
                <UnifiedView lines={sectionLines} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export { DiffViewer, DiffStats }
export type { DiffViewerProps, DiffStatsProps, DiffMode }
